<template>
    <div ref="root" class="flex flex-col" aria-label="Profile suggestions" :aria-busy="disabled">
        <template v-for="section in sections" :key="section.action">
            <div v-if="section.action === 'share' && !ownProfileShare" class="suggestions-section">
                <ProfileLinkCreateProfilePrompt
                    :locked-suggestion-count="section.items.length"
                    :profile-link-sharing-enabled="profileLinkSharingEnabled"
                    @shown="emit('create-profile-shown')"
                    @cta-click="emit('create-profile-click')"
                    @explainer-click="emit('explainer-click')" />
            </div>
            <div v-else-if="section.items.length" class="suggestions-section">
                <h4 class="body-small-bold text-ink">{{ section.title }}</h4>
                <p v-if="section.action === 'share'" class="body-small text-ink-muted mb-2">{{ sharingDisclosure }}</p>
                <TransitionGroup tag="div" name="suggestion-row" class="flex flex-col">
                    <div
                        v-for="suggestion in section.items"
                        :key="suggestion.target_user_id"
                        :data-suggestion-row="suggestion.target_user_id"
                        class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 mb-3 last:mb-0">
                        <ProfileLinkSuggestionsRow
                            :suggestion="suggestion"
                            :status="statuses[suggestion.target_user_id] ?? 'idle'"
                            :own-profile-share="ownProfileShare"
                            :org-has-ms-teams="orgHasMsTeams"
                            :share-source="shareSource"
                            :disabled="disabled"
                            compact
                            @linked="(s) => emit('linked', s)"
                            @link-failed="(s) => emit('link-failed', s)"
                            @shared="(s, target) => emit('shared', s, target)"
                            @share-failed="(s, reason) => emit('share-failed', s, reason)"
                            @dismiss-started="(s) => emit('dismiss-started', s)"
                            @dismissed="handleDismissed"
                            @dismiss-failed="handleDismissFailed" />
                    </div>
                </TransitionGroup>
                <BaseButton
                    v-if="section.action === 'link' && canLinkAll"
                    theme="neutral"
                    class="link-all"
                    :disabled="disabled || isLinkingAll || linkAllBlocked"
                    :aria-label="isLinkingAll ? 'Linking profiles' : undefined"
                    data-test-id="profile-link-all-btn"
                    @click="emit('link-all')">
                    <LoadingSpinner v-if="isLinkingAll" class="size-4" />
                    <span v-else>Link all</span>
                </BaseButton>
                <!-- Lifetime guard, not the message condition: the region is created empty for the
                     link section and only its text changes. -->
                <template v-if="section.action === 'link'">
                    <span class="sr-only" role="status">{{ isLinkingAll ? "Linking profiles…" : "" }}</span>
                </template>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import type { ProfileShareSource } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ProfileLinkCreateProfilePrompt from "~vue/Labs/ProfileLinkCreateProfilePrompt.vue";
import ProfileLinkSuggestionsRow from "~vue/Labs/ProfileLinkSuggestionsRow.vue";
import type { OwnProfileShare, ProfileLinkSuggestion, ShareFailureReason, ShareTarget, SuggestionStatus } from "~vue/Labs/profileLinkTypes";
import { computed, nextTick, useTemplateRef } from "vue";

const { sharingDisclosure } = useProfileSharingCopy();

const props = defineProps<{
    suggestions: ProfileLinkSuggestion[];
    statuses: Record<string, SuggestionStatus>;
    ownProfileShare: OwnProfileShare | null;
    orgHasMsTeams: boolean;
    shareSource: ProfileShareSource;
    profileLinkSharingEnabled: boolean;
    canLinkAll: boolean;
    isLinkingAll: boolean;
    linkAllBlocked: boolean;
    disabled: boolean;
}>();

const emit = defineEmits<{
    linked: [suggestion: ProfileLinkSuggestion];
    "link-failed": [suggestion: ProfileLinkSuggestion];
    shared: [suggestion: ProfileLinkSuggestion, target: ShareTarget];
    "share-failed": [suggestion: ProfileLinkSuggestion, reason: ShareFailureReason];
    "dismiss-started": [suggestion: ProfileLinkSuggestion];
    dismissed: [suggestion: ProfileLinkSuggestion];
    "dismiss-failed": [suggestion: ProfileLinkSuggestion];
    "focus-fallback": [];
    "link-all": [];
    "create-profile-shown": [];
    "create-profile-click": [];
    "explainer-click": [];
}>();

const linkSuggestions = computed(() => props.suggestions.filter((s) => s.action === "link"));
const sections = computed(() => [
    { action: "link", title: "Suggested Profiles", items: linkSuggestions.value },
    { action: "share", title: "Share yours, request theirs", items: props.suggestions.filter((s) => s.action === "share") },
]);

const root = useTemplateRef<HTMLElement>("root");

// The Dismiss button is disabled while pending, which can drop focus to <body>; give it back for a retry.
async function handleDismissFailed(suggestion: ProfileLinkSuggestion) {
    const active = document.activeElement;
    emit("dismiss-failed", suggestion);
    if (active && active !== document.body) {
        return;
    }
    await nextTick();
    root.value?.querySelector<HTMLElement>(`[data-test-id="profile-dismiss-btn-${suggestion.target_user_id}"]`)?.focus();
}

// The dismissed row (and its focused button) is removed, so hand focus to the neighbouring link row,
// falling back to Link all, the first share action, then the card. The button is disabled while the
// request is pending, which can drop focus to <body>; that still counts as focus on the row.
async function handleDismissed(suggestion: ProfileLinkSuggestion) {
    const index = linkSuggestions.value.findIndex((s) => s.target_user_id === suggestion.target_user_id);
    const row = root.value?.querySelector(`[data-suggestion-row="${suggestion.target_user_id}"]`);
    const active = document.activeElement;
    const focusWasOnRow = !active || active === document.body || !!row?.contains(active);
    emit("dismissed", suggestion);
    if (!focusWasOnRow) {
        return;
    }
    // Dismissing the last row unmounts this component, and emits from an unmounted component are dropped.
    if (!props.suggestions.some((s) => s.target_user_id !== suggestion.target_user_id)) {
        emit("focus-fallback");
        return;
    }
    await nextTick();
    const remaining = linkSuggestions.value.filter((s) => s.target_user_id !== suggestion.target_user_id);
    const next = remaining[Math.min(index, remaining.length - 1)];
    const selectors = [
        next && `[data-test-id="profile-link-btn-${next.target_user_id}"]:not(:disabled)`,
        '[data-test-id="profile-link-all-btn"]:not(:disabled)',
        '[data-test-id^="profile-request-btn-"]:not(:disabled)',
    ].filter(Boolean) as string[];
    for (const selector of selectors) {
        const target = root.value?.querySelector<HTMLElement>(selector);
        if (target) {
            target.focus();
            return;
        }
    }
    emit("focus-fallback");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .suggestions-section {
        @apply flex flex-col gap-4 border-t border-border p-6;
    }

    .link-all {
        @apply flex w-full items-center justify-center;
        --font-weight-button: var(--font-weight-normal);
    }

    /* Row removal (linked or dismissed): fade out first, then collapse so the rows below slide up. */
    .suggestion-row-leave-active {
        overflow: hidden;
    }

    .suggestion-row-leave-to {
        opacity: 0;
        max-height: 0;
        margin-bottom: 0;
    }

    @media (prefers-reduced-motion: no-preference) {
        .suggestion-row-leave-active {
            transition:
                opacity 160ms ease,
                max-height 220ms ease 160ms,
                margin-bottom 220ms ease 160ms;
        }

        .suggestion-row-leave-from {
            max-height: 4rem;
        }
    }
}
</style>
